import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AppTablePagination from "../../component/common/appTablePagination";
import React, { useCallback, useEffect, useState } from "react";
import { OrderData } from "../../../types/saleOrder";
import SaleOrderServices from "../../../services/SalesOrderService";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import OrderDetailsModal from "./OrderDetailsModal";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import { useTranslation } from "react-i18next";
import CircleIcon from "@mui/icons-material/Circle";
import { onPaymentsEvent } from "../../../services/paymentsEvents";
import {
  getOrderPaymentBadgeLabelKey,
  ORDER_PAYMENT_BADGE_STYLES,
  resolveOrderPaymentBadgeTone,
} from "../../../helper/orderPaymentBadge";
import {
  clearOrderPaymentAbandoned,
  loadActivePaymentSession,
} from "../../../utils/abandonedOrderPayments";
import { loadQiCardPending } from "../../../utils/qicardStorage";
import { isUnpaidHeldCheckoutOrder } from "../../../utils/checkoutSession";
import { NJIM } from "../../../theme/njimTheme";


interface OrderHistoryProps {
  refreshKey: number;
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ refreshKey }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");
  const [loading, setLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderData | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 10,
    sortBy: "CreatedAt",
    sortDescending: true,
  });

  const getStatusStyles = (status: string | number | undefined) => {
    const statusString = String(status).toLowerCase();
    if (statusString === "pending") {
      return {
        background: "rgba(245,158,11,0.08)",
        color: NJIM.warning,
        border: "1px solid rgba(245,158,11,0.2)",
      };
    }
    if (statusString === "confirmed") {
      return {
        background: NJIM.mintWash,
        color: NJIM.ink,
        border: "1px solid rgba(93,183,167,0.3)",
      };
    }
    if (statusString === "shipped") {
      return {
        background: "rgba(29,67,86,0.1)",
        color: NJIM.info,
        border: "1px solid rgba(29,67,86,0.2)",
      };
    }
    if (statusString === "delivered") {
      return {
        background: "rgba(22,163,74,0.08)",
        color: NJIM.success,
        border: "1px solid rgba(22,163,74,0.2)",
      };
    }
    if (statusString === "failed") {
      return {
        background: "rgba(220,38,38,0.08)",
        color: NJIM.danger,
        border: "1px solid rgba(220,38,38,0.2)",
      };
    }
    if (statusString === "notdelivered") {
      return {
        background: NJIM.muted,
        color: NJIM.textSecondary,
        border: `1px solid ${NJIM.border}`,
      };
    }
    return {
      background: NJIM.muted,
      color: NJIM.textSecondary,
      border: `1px solid ${NJIM.border}`,
    };
  };
  const getLocalizedStatus = (status: string | number | undefined) => {
    const statusValue = String(status ?? "").trim();
    if (!statusValue) return "-";

    const normalizedStatus = statusValue
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .toLowerCase()
      .replace(/\s+/g, "");

    const fallbackStatus = statusValue.replace(/([a-z])([A-Z])/g, "$1 $2");
    return t(`status.${normalizedStatus}`, { defaultValue: fallbackStatus });
  };
  const fetchOrderList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        keyword: "",
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
        sortBy: filters.sortBy,
        sortDescending: filters.sortDescending,
      };
      const response = await SaleOrderServices.getSaleOrderList(params);
      const finalResponse = response?.data?.items ?? [];
      finalResponse.forEach((order: OrderData) => {
        const status = String(order.paymentStatus ?? "").toLowerCase();
        if (order.isSynced || status.includes("paid") || status.includes("success")) {
          clearOrderPaymentAbandoned(order.id);
        }
      });
      // A gateway payment (QiCard redirect, or a submitted card) is in flight for this order.
      // QiCard confirms by webhook, so the order can still read PENDING for a while after the
      // customer paid — it must stay visible instead of being hidden as an unpaid draft.
      const parkedQi = loadQiCardPending();
      const activeSession = loadActivePaymentSession();
      const hasPaymentInFlight = (orderId: unknown) => {
        const id = String(orderId ?? "");
        return (
          (!!parkedQi?.orderId && String(parkedQi.orderId) === id) ||
          (!!activeSession?.gatewaySubmittedAt && activeSession.orderId === id)
        );
      };
      const visible = finalResponse.filter((order: OrderData) => {
        if (!isUnpaidHeldCheckoutOrder(order.id)) return true;
        if (hasPaymentInFlight(order.id)) return true;
        const status = String(order.paymentStatus ?? "").toLowerCase();
        return Boolean(order.isSynced) || status.includes("paid") || status.includes("success");
      });
      setOrders(visible);
      setTotalCount(
        Math.max(0, Number(response?.data?.totalCount ?? 0) - (finalResponse.length - visible.length))
      );
    } catch (error) {
      console.error("Error fetching Banner Image:", error);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters.pageNumber, filters.pageSize, filters.sortBy, filters.sortDescending]);

  useEffect(() => {
    fetchOrderList();
  }, [filters, refreshKey, fetchOrderList]);

  useEffect(() => {
    return onPaymentsEvent(() => {
      fetchOrderList();
    });
  }, [fetchOrderList]);
  const handleViewDetails = async (id: string) => {
    try {
      setLoading(true);
      const response = await SaleOrderServices.getSaleOrderById(id);
      setSelectedOrder(response.data);
      setOpenModal(true);
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Box>
        <TableContainer>
          <Typography sx={{ fontSize: "16px", fontWeight: 600, color: NJIM.textPrimary, marginBottom: 3 }} gutterBottom>
            {t("salesOrder.orderHistory")}
          </Typography>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: 5,
              }}
            >
              <CircularProgress size={44} sx={{ color: NJIM.mint }} />
            </Box>
          ) : (
            <>
              <Table
                sx={{
                  direction: isRTL ? "rtl" : "ltr",
                  border: `1px solid ${NJIM.border}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                  "& .MuiTableCell-root": {
                    padding: "12px 14px",
                    borderBottom: `1px solid ${NJIM.border}`,
                    fontSize: "13px",
                    textAlign: isRTL ? "right" : "left",
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: NJIM.muted }}>
                    <TableCell sx={{ width: 40 }}></TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      {t("salesOrder.orderNumber")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      {t("common.date")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      {t("common.items")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      {t("common.amount")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      {t("common.status")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      {t("common.paymentStatus")}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => {
                    const paymentTone = resolveOrderPaymentBadgeTone(
                      order.paymentStatus,
                      order.isSynced,
                      order.id
                    );
                    const paymentStyles = ORDER_PAYMENT_BADGE_STYLES[paymentTone];
                    const paymentLabel = t(getOrderPaymentBadgeLabelKey(paymentTone));
                    return (
                      <TableRow
                        key={order.id}
                        sx={{
                          transition: "background 0.2s ease",
                          "&:hover": { bgcolor: NJIM.muted },
                        }}
                      >
                        <TableCell sx={{ textAlign: "center !important" }}>
                          {order.isSynced === true ? (
                            <CircleIcon sx={{ color: NJIM.success, fontSize: 14 }} />
                          ) : (
                            <CircleIcon sx={{ color: NJIM.warning, fontSize: 14 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>{order.number}</TableCell>
                        <TableCell sx={{ color: NJIM.textSecondary }}>
                          {order.date
                            ? new Date(order.date).toISOString().split("T")[0]
                            : "-"}
                        </TableCell>
                        <TableCell sx={{ color: NJIM.textSecondary }}>{order.items?.length}</TableCell>
                        <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>
                          {CURRENCY_SYMBOL}
                          {order.amount?.toLocaleString('en-US') ?? "0"}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              py: 0.5,
                              px: 1.2,
                              borderRadius: "999px",
                              width: "fit-content",
                              fontSize: "12px",
                              fontWeight: 500,
                              ...getStatusStyles(order.status),
                            }}
                          >
                            {getLocalizedStatus(order.status)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              py: 0.5,
                              px: 1.2,
                              borderRadius: "999px",
                              width: "fit-content",
                              fontSize: "12px",
                              fontWeight: 600,
                              ...paymentStyles,
                            }}
                          >
                            {paymentLabel}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            className="njim-secondary-btn"
                            {...(isRTL
                              ? { endIcon: <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} /> }
                              : { startIcon: <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} /> })}
                            onClick={() => handleViewDetails(String(order.id))}
                            sx={{
                              fontSize: "12px",
                              gap: "6px",
                              "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                margin: 0,
                              },
                              "& .MuiButton-endIcon": {
                                marginInlineStart: "6px",
                              },
                            }}
                          >
                            {t("common.viewDetails")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Grid
                style={{
                  justifyContent: "flex-end",
                  alignItems: "center",
                  display: "flex",
                  marginTop: 16,
                  padding: 8,
                }}
              >
                <AppTablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber - 1}
                  onPageChange={(_, newPage) =>
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: newPage + 1,
                    }))
                  }
                  onRowsPerPageChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      pageSize: parseInt(e.target.value, 10),
                      pageNumber: 1,
                    }))
                  }
                  sx={{
                    color: NJIM.textSecondary,
                    "& .MuiSelect-icon": { color: NJIM.textSecondary },
                    "& .MuiSvgIcon-root": { color: NJIM.textSecondary },
                  }}
                />
              </Grid>
            </>
          )}
        </TableContainer>
      </Box>
      <OrderDetailsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        order={selectedOrder}
      />
    </>
  );
};

export default OrderHistory;
